/**
 * 前端 API 请求客户端
 * 对接根目录 api/ PHP 后端
 */

import { getDeviceFingerprint } from '@/utils/fingerprint'

const API_BASE = process.env.VUE_APP_API_BASE || '/api'
const TOKEN_KEY = 'blog_api_token'

export function getApiToken() {
  return localStorage.getItem(TOKEN_KEY) || ''
}

function getToken() {
  return getApiToken()
}

function applyAuthHeaders(headers = {}) {
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
    headers['X-Auth-Token'] = token
  }
  return headers
}

export function setApiToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

export function getApiBase() {
  return API_BASE
}

function buildQueryString(params = {}) {
  const clean = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    const text = String(value).trim()
    if (!text || text === 'undefined' || text === 'null') return
    clean[key] = value
  })
  const qs = new URLSearchParams(clean).toString()
  return qs ? `?${qs}` : ''
}

/** 解析 Content-Disposition，优先 RFC5987 filename*（UTF-8） */
function parseContentDispositionFilename(disp) {
  if (!disp) return ''
  const star = disp.match(/filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;\s]+)/i)
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].replace(/["']/g, '').trim())
    } catch (e) { /* fall through */ }
  }
  const quoted = disp.match(/filename\s*=\s*"((?:\\.|[^"\\])*)"/i)
  if (quoted && quoted[1]) {
    return quoted[1].replace(/\\"/g, '"')
  }
  const plain = disp.match(/filename\s*=\s*([^;]+)/i)
  if (plain && plain[1]) {
    return plain[1].trim().replace(/^["']|["']$/g, '')
  }
  return ''
}

async function sha256File(file) {
  if (!file || typeof crypto === 'undefined' || !crypto.subtle) return ''
  const buf = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

async function request(path, options = {}) {
  const fingerprint = getDeviceFingerprint()
  const headers = {
    'Content-Type': 'application/json',
    'X-Device-Fingerprint': fingerprint,
    ...(options.headers || {})
  }
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
    // 部分 Nginx 会丢掉 Authorization，备用头保证登录态
    headers['X-Auth-Token'] = token
  }

  let body = options.body
  if (body && typeof body === 'string') {
    try {
      const parsed = JSON.parse(body)
      parsed.fingerprint = fingerprint
      body = JSON.stringify(parsed)
    } catch {
      // 保持原 body
    }
  }

  let res
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      body,
      headers
    })
  } catch (networkErr) {
    const err = new Error(networkErr.message || '网络异常')
    err.code = 'NETWORK'
    err.httpStatus = 0
    throw err
  }

  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.code !== 0) {
    const err = new Error(json.message || '请求失败')
    err.code = json.code
    err.httpStatus = res.status
    err.response = json
    throw err
  }
  return json
}

export const api = {
  // 站点
  getSiteConfig: (options = {}) => request('/site/config', { cache: 'no-store', ...options }),

  // 友链
  getFriendLinks: () => request('/friend-links'),
  trackFriendLinkVisit: data => request('/friend-links/track', { method: 'POST', body: JSON.stringify(data) }),
  trackFriendLinkClick: id => request('/friend-links/click', { method: 'POST', body: JSON.stringify({ id }) }),

  // 在线工具 API
  toolIpLookup: ip => request(`/tools/ip-lookup?ip=${encodeURIComponent(ip)}`),
  toolDomainLookup: domain => request(`/tools/domain-lookup?domain=${encodeURIComponent(domain)}`),
  toolIpEngineStatus: () => request('/tools/ip-engine/status'),
  toolSpeedTestPing: () => request(`/tools/speed-test/ping?t=${Date.now()}`, { cache: 'no-store' }),
  toolSpeedTestDownloadUrl: (sizeMb = 2) =>
    `${getApiBase()}/tools/speed-test/download?size=${encodeURIComponent(sizeMb)}&t=${Date.now()}`,
  toolSpeedTestUpload: async (blob, signal) => {
    const headers = { 'Content-Type': 'application/octet-stream' }
    applyAuthHeaders(headers)
    const res = await fetch(`${getApiBase()}/tools/speed-test/upload?t=${Date.now()}`, {
      method: 'POST',
      headers,
      body: blob,
      cache: 'no-store',
      signal
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传测速失败')
      err.code = json.code
      throw err
    }
    return json
  },
  toolNetProbe: (data) => request('/tools/net-probe', { method: 'POST', body: JSON.stringify(data || {}) }),
  lanSpeedCreateRoom: () => request('/tools/lan-speed/rooms', { method: 'POST', body: '{}' }),
  lanSpeedGetRoom: (code) => request(`/tools/lan-speed/rooms/${encodeURIComponent(code)}`),
  lanSpeedPutOffer: (code, sdp) =>
    request(`/tools/lan-speed/rooms/${encodeURIComponent(code)}/offer`, {
      method: 'PUT',
      body: JSON.stringify(sdp || {})
    }),
  lanSpeedPutAnswer: (code, sdp) =>
    request(`/tools/lan-speed/rooms/${encodeURIComponent(code)}/answer`, {
      method: 'PUT',
      body: JSON.stringify(sdp || {})
    }),
  lanSpeedPostIce: (code, data) =>
    request(`/tools/lan-speed/rooms/${encodeURIComponent(code)}/ice`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanSpeedPoll: (code, params = {}) => {
    const qs = new URLSearchParams({
      role: params.role || 'host',
      offerSeq: String(params.offerSeq || 0),
      answerSeq: String(params.answerSeq || 0),
      iceSeq: String(params.iceSeq || 0),
      t: String(Date.now())
    }).toString()
    return request(`/tools/lan-speed/rooms/${encodeURIComponent(code)}/poll?${qs}`, { cache: 'no-store' })
  },
  lanSpeedClose: (code) =>
    request(`/tools/lan-speed/rooms/${encodeURIComponent(code)}/close`, {
      method: 'POST',
      body: '{}'
    }),

  lanTransferCreateRoom: () => request('/tools/lan-transfer/rooms', { method: 'POST', body: '{}' }),
  lanTransferJoin: (code, data) =>
    request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/join`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanTransferHeartbeat: (code, data) =>
    request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanTransferLeave: (code, data) =>
    request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/leave`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanTransferSignal: (code, data) =>
    request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/signal`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanTransferPoll: (code, params = {}) => {
    const qs = new URLSearchParams({
      peerId: params.peerId || '',
      since: String(params.since || 0),
      msgSince: String(params.msgSince || 0),
      t: String(Date.now())
    }).toString()
    return request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/poll?${qs}`, { cache: 'no-store' })
  },
  lanTransferMsg: (code, data) =>
    request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/msg`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanTransferInitFile: (code, data) =>
    request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/files`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanTransferPutFileChunk: async (code, fileId, { peerId, index, blob }) => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const fd = new FormData()
    fd.append('peerId', peerId)
    fd.append('index', String(index))
    fd.append('chunk', blob, `part-${index}.bin`)
    const res = await fetch(
      `${API_BASE}/tools/lan-transfer/rooms/${encodeURIComponent(code)}/files/${encodeURIComponent(fileId)}/chunk`,
      { method: 'POST', headers, body: fd }
    )
    const json = await res.json().catch(() => ({}))
    if (!res.ok || (json && json.code !== 0)) {
      throw new Error((json && json.message) || '分块上传失败')
    }
    return json.data || json
  },
  lanTransferCompleteFile: (code, fileId, data) =>
    request(`/tools/lan-transfer/rooms/${encodeURIComponent(code)}/files/${encodeURIComponent(fileId)}/complete`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),
  lanTransferDownloadUrl: (code, fileId, peerId) =>
    `${API_BASE}/tools/lan-transfer/rooms/${encodeURIComponent(code)}/files/${encodeURIComponent(fileId)}?peerId=${encodeURIComponent(peerId)}&t=${Date.now()}`,

  // 文章
  getArticles: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/articles${qs ? `?${qs}` : ''}`)
  },
  getArticle: id => request(`/articles/${id}`),

  // 认证
  login: data => request('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  register: data => request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request('/auth/me'),
  sendRegisterCode: email => request('/auth/register-code', { method: 'POST', body: JSON.stringify({ email }) }),
  sendResetCode: email => request('/auth/reset-code', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: data => request('/auth/reset-password', { method: 'POST', body: JSON.stringify(data) }),
  wxLogin: data => request('/auth/wx/login', { method: 'POST', body: JSON.stringify(data) }),
  createQrLogin: () => request('/auth/qr/create', { method: 'POST', body: JSON.stringify({}) }),
  pollQrLogin: ticket => request(`/auth/qr/status?ticket=${encodeURIComponent(ticket)}`),
  cancelQrLogin: ticket => request('/auth/qr/cancel', { method: 'POST', body: JSON.stringify({ ticket }) }),
  confirmQrLogin: ticket => request('/auth/qr/confirm', { method: 'POST', body: JSON.stringify({ ticket }) }),
  createWxBind: () => request('/auth/wx/bind/create', { method: 'POST', body: JSON.stringify({}) }),
  pollWxBind: ticket => request(`/auth/wx/bind/status?ticket=${encodeURIComponent(ticket)}`),
  cancelWxBind: ticket => request('/auth/wx/bind/cancel', { method: 'POST', body: JSON.stringify({ ticket }) }),
  unbindWx: () => request('/user/wx/unbind', { method: 'POST', body: JSON.stringify({}) }),

  // 用户
  updateProfile: data => request('/user/profile', { method: 'PUT', body: JSON.stringify(data) }),
  uploadAvatar: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const headers = {
      'X-Device-Fingerprint': getDeviceFingerprint()
    }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/user/avatar/upload`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  changePassword: data => request('/user/password', { method: 'PUT', body: JSON.stringify(data) }),
  getOrders: () => request('/user/orders'),
  activateMember: planId => request('/member/activate', { method: 'POST', body: JSON.stringify({ planId }) }),
  redeemCdk: code => request('/member/redeem', { method: 'POST', body: JSON.stringify({ code }) }),
  purchaseArticle: (id, price) => request(`/articles/${id}/purchase`, {
    method: 'POST',
    body: JSON.stringify({ price })
  }),
  checkin: () => request('/user/checkin', { method: 'POST' }),
  getPointsLog: (limit = 50) => request(`/user/points-log?limit=${limit}`),

  // WiFi 分享
  getWifiList: () => request('/tools/wifi'),
  getWifiQuota: () => request('/tools/wifi/quota'),
  createWifi: data => request('/tools/wifi', { method: 'POST', body: JSON.stringify(data) }),
  updateWifi: (id, data) => request(`/tools/wifi/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteWifi: id => request(`/tools/wifi/${id}`, { method: 'DELETE' }),
  wifiQrcode: id => request(`/tools/wifi/${id}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  getWifiShare: code => request(`/tools/wifi/share/${encodeURIComponent(code)}`),
  getWifiShareCard: code => request(`/tools/wifi/share/${encodeURIComponent(code)}/card`),
  adminGetWifiSettings: () => request('/admin/wifi-settings'),
  adminUpdateWifiSettings: settings => request('/admin/wifi-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetWifiLinks: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/wifi-links${qs ? `?${qs}` : ''}`)
  },
  adminGetWifiLink: id => request(`/admin/wifi-links/${id}`),
  adminUpdateWifiLink: (id, data) => request(`/admin/wifi-links/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteWifiLink: id => request(`/admin/wifi-links/${id}`, { method: 'DELETE' }),

  // 礼薄
  getGiftBooks: () => request('/tools/gift-books'),
  getGiftBookQuota: () => request('/tools/gift-books/quota'),
  searchGiftBooks: (params = {}) => request(`/tools/gift-books/search${buildQueryString(params)}`),
  createGiftBook: data => request('/tools/gift-books', { method: 'POST', body: JSON.stringify(data) }),
  getGiftBook: (id, params = {}) => request(`/tools/gift-books/${id}${buildQueryString(params)}`),
  updateGiftBook: (id, data) => request(`/tools/gift-books/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGiftBook: id => request(`/tools/gift-books/${id}`, { method: 'DELETE' }),
  createGiftEntry: (bookId, data) => request(`/tools/gift-books/${bookId}/entries`, { method: 'POST', body: JSON.stringify(data) }),
  updateGiftEntry: (bookId, entryId, data) => request(`/tools/gift-books/${bookId}/entries/${entryId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGiftEntry: (bookId, entryId) => request(`/tools/gift-books/${bookId}/entries/${entryId}`, { method: 'DELETE' }),
  exportGiftBook: async (id, params = {}) => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const qs = buildQueryString({
      format: params.format || 'xlsx',
      q: params.q || '',
      guestName: params.guestName || '',
      amountMin: params.amountMin !== undefined && params.amountMin !== null ? params.amountMin : '',
      amountMax: params.amountMax !== undefined && params.amountMax !== null ? params.amountMax : '',
      sort: params.sort || 'default'
    })
    const res = await fetch(`${API_BASE}/tools/gift-books/${id}/export${qs}`, { headers })
    if (!res.ok) {
      let msg = '导出失败'
      try {
        const j = await res.json()
        if (j && j.message) msg = j.message
      } catch (e) { /* ignore */ }
      throw new Error(msg)
    }
    const blob = await res.blob()
    const disp = res.headers.get('Content-Disposition') || ''
    const fromHeader = parseContentDispositionFilename(disp)
    const ext = params.format === 'csv' ? 'csv' : 'xlsx'
    return { blob, filename: fromHeader || `gift-book-${id}.${ext}` }
  },
  createGiftShare: data => request('/tools/gift-shares', { method: 'POST', body: JSON.stringify(data) }),
  updateGiftShare: (id, data) => request(`/tools/gift-shares/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGiftShare: id => request(`/tools/gift-shares/${id}`, { method: 'DELETE' }),
  giftShareQrcode: id => request(`/tools/gift-shares/${id}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  getGiftShareCard: code => request(`/tools/gift-shares/view/${encodeURIComponent(code)}/card`),
  viewGiftShare: (code, data = {}) => request(`/tools/gift-shares/view/${encodeURIComponent(code)}`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  adminGetGiftBookSettings: () => request('/admin/gift-book-settings'),
  adminUpdateGiftBookSettings: settings => request('/admin/gift-book-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetGiftBooks: (params = {}) => request(`/admin/gift-books${buildQueryString(params)}`),
  adminUpdateGiftBook: (id, data) => request(`/admin/gift-books/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteGiftBook: id => request(`/admin/gift-books/${id}`, { method: 'DELETE' }),
  adminUpdateGiftShare: (id, data) => request(`/admin/gift-shares/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteGiftShare: id => request(`/admin/gift-shares/${id}`, { method: 'DELETE' }),

  // 点餐菜单
  getOrderMenus: () => request('/tools/order-menus'),
  getOrderMenuQuota: () => request('/tools/order-menus/quota'),
  createOrderMenu: data => request('/tools/order-menus', { method: 'POST', body: JSON.stringify(data) }),
  getOrderMenu: id => request(`/tools/order-menus/${id}`),
  updateOrderMenu: (id, data) => request(`/tools/order-menus/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteOrderMenu: id => request(`/tools/order-menus/${id}`, { method: 'DELETE' }),
  uploadOrderMenuImage: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const fingerprint = getDeviceFingerprint()
    fd.append('fingerprint', fingerprint)
    // 勿设 Content-Type，由浏览器自动带 multipart boundary，否则 PHP 收不到 $_FILES
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/tools/order-menus/upload-image`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  createOrderDish: (menuId, data) => request(`/tools/order-menus/${menuId}/dishes`, { method: 'POST', body: JSON.stringify(data) }),
  updateOrderDish: (menuId, dishId, data) => request(`/tools/order-menus/${menuId}/dishes/${dishId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteOrderDish: (menuId, dishId) => request(`/tools/order-menus/${menuId}/dishes/${dishId}`, { method: 'DELETE' }),
  createOrderTable: (menuId, data) => request(`/tools/order-menus/${menuId}/tables`, { method: 'POST', body: JSON.stringify(data) }),
  updateOrderTable: (menuId, tableId, data) => request(`/tools/order-menus/${menuId}/tables/${tableId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteOrderTable: (menuId, tableId) => request(`/tools/order-menus/${menuId}/tables/${tableId}`, { method: 'DELETE' }),
  orderMenuQrcode: id => request(`/tools/order-menus/${id}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  orderTableQrcode: (menuId, tableId) => request(`/tools/order-menus/${menuId}/tables/${tableId}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  getOrderMenuOrders: (menuId, params = {}) => request(`/tools/order-menus/${menuId}/orders${buildQueryString(params)}`),
  pollOrderMenuOrders: (menuId, params = {}) => request(`/tools/order-menus/${menuId}/orders/poll${buildQueryString(params)}`),
  getOrderMenuAlerts: () => request('/tools/order-menus/alerts'),
  printOrderMenuOrder: (menuId, orderId) => request(`/tools/order-menus/${menuId}/orders/${orderId}/print`, { method: 'POST', body: '{}' }),
  testOrderMenuPrinter: (menuId, data = {}) => request(`/tools/order-menus/${menuId}/printer/test`, { method: 'POST', body: JSON.stringify(data) }),
  previewOrderMenuPrinter: (menuId, data = {}) => request(`/tools/order-menus/${menuId}/printer/preview`, { method: 'POST', body: JSON.stringify(data) }),
  updateOrderMenuOrder: (menuId, orderId, data) => request(`/tools/order-menus/${menuId}/orders/${orderId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getOrderMenuCard: code => request(`/tools/order-menus/view/${encodeURIComponent(code)}/card`),
  viewOrderMenu: (code, params = {}) => request(`/tools/order-menus/view/${encodeURIComponent(code)}${buildQueryString(params)}`),
  placeOrderMenuOrder: (code, data) => request(`/tools/order-menus/view/${encodeURIComponent(code)}/orders`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  myOrderMenuOrders: (code, data = {}) => request(`/tools/order-menus/view/${encodeURIComponent(code)}/my-orders`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  adminGetOrderMenuSettings: () => request('/admin/order-menu-settings'),
  adminUpdateOrderMenuSettings: settings => request('/admin/order-menu-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetOrderMenus: (params = {}) => request(`/admin/order-menus${buildQueryString(params)}`),
  adminGetOrderMenu: id => request(`/admin/order-menus/${id}`),
  adminUpdateOrderMenu: (id, data) => request(`/admin/order-menus/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteOrderMenu: id => request(`/admin/order-menus/${id}`, { method: 'DELETE' }),

  // 电费记录 / 电房
  getElectricRooms: () => request('/tools/electric-rooms'),
  getElectricRoomQuota: () => request('/tools/electric-rooms/quota'),
  createElectricRoom: data => request('/tools/electric-rooms', { method: 'POST', body: JSON.stringify(data) }),
  getElectricRoom: id => request(`/tools/electric-rooms/${id}`),
  updateElectricRoom: (id, data) => request(`/tools/electric-rooms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteElectricRoom: id => request(`/tools/electric-rooms/${id}`, { method: 'DELETE' }),
  electricRoomQrcode: id => request(`/tools/electric-rooms/${id}/qrcode`, { method: 'POST', body: '{}' }),
  createElectricMeter: (roomId, data) => request(`/tools/electric-rooms/${roomId}/meters`, { method: 'POST', body: JSON.stringify(data) }),
  updateElectricMeter: (roomId, meterId, data) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteElectricMeter: (roomId, meterId) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}`, { method: 'DELETE' }),
  electricMeterQrcode: (roomId, meterId) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}/qrcode`, { method: 'POST', body: '{}' }),
  createElectricReading: (roomId, meterId, data) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}/readings`, { method: 'POST', body: JSON.stringify(data) }),
  updateElectricReading: (roomId, meterId, readingId, data) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}/readings/${readingId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteElectricReading: (roomId, meterId, readingId) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}/readings/${readingId}`, { method: 'DELETE' }),
  getElectricMeterOpenToken: (roomId, meterId) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}/open-token`),
  rotateElectricMeterOpenToken: (roomId, meterId) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}/open-token`, { method: 'POST', body: '{}' }),
  revokeElectricMeterOpenToken: (roomId, meterId) => request(`/tools/electric-rooms/${roomId}/meters/${meterId}/open-token`, { method: 'DELETE' }),
  exportElectricRoom: async (id, params = {}) => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const qs = buildQueryString({
      format: params.format || 'xlsx',
      mode: params.mode || 'current',
      periodYm: params.periodYm || '',
      meterId: params.meterId || ''
    })
    const res = await fetch(`${API_BASE}/tools/electric-rooms/${id}/export${qs}`, { headers })
    if (!res.ok) {
      let msg = '导出失败'
      try {
        const j = await res.json()
        if (j && j.message) msg = j.message
      } catch (e) { /* ignore */ }
      throw new Error(msg)
    }
    const blob = await res.blob()
    const disp = res.headers.get('Content-Disposition') || ''
    const m = disp.match(/filename="?([^"]+)"?/i)
    const ext = (params.format === 'csv') ? 'csv' : 'xlsx'
    return { blob, filename: (m && m[1]) || `electric-room-${id}.${ext}` }
  },
  getElectricRoomCard: code => request(`/tools/electric-rooms/view/${encodeURIComponent(code)}/card`),
  viewElectricRoom: (code, data = {}) => request(`/tools/electric-rooms/view/${encodeURIComponent(code)}`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  adminGetElectricRoomSettings: () => request('/admin/electric-room-settings'),
  adminUpdateElectricRoomSettings: settings => request('/admin/electric-room-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetElectricRooms: (params = {}) => request(`/admin/electric-rooms${buildQueryString(params)}`),
  adminGetElectricRoom: id => request(`/admin/electric-rooms/${id}`),
  adminUpdateElectricRoom: (id, data) => request(`/admin/electric-rooms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteElectricRoom: id => request(`/admin/electric-rooms/${id}`, { method: 'DELETE' }),

  // 任务提交 / 任务板
  getTaskBoards: () => request('/tools/task-boards'),
  getTaskBoardQuota: () => request('/tools/task-boards/quota'),
  createTaskBoard: data => request('/tools/task-boards', { method: 'POST', body: JSON.stringify(data) }),
  getTaskBoard: (id, params = {}) => request(`/tools/task-boards/${id}${buildQueryString(params)}`),
  updateTaskBoard: (id, data) => request(`/tools/task-boards/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTaskBoard: id => request(`/tools/task-boards/${id}`, { method: 'DELETE' }),
  addTaskBoardMember: (id, data) => request(`/tools/task-boards/${id}/members`, { method: 'POST', body: JSON.stringify(data) }),
  updateTaskBoardMember: (id, memberId, data) => request(`/tools/task-boards/${id}/members/${memberId}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeTaskBoardMember: (id, memberId) => request(`/tools/task-boards/${id}/members/${memberId}`, { method: 'DELETE' }),
  createTaskBoardShare: (id, data) => request(`/tools/task-boards/${id}/shares`, { method: 'POST', body: JSON.stringify(data) }),
  deleteTaskBoardShare: (id, shareId) => request(`/tools/task-boards/${id}/shares/${shareId}`, { method: 'DELETE' }),
  createTaskItem: (id, data) => request(`/tools/task-boards/${id}/tasks`, { method: 'POST', body: JSON.stringify(data) }),
  updateTaskItem: (id, taskId, data) => request(`/tools/task-boards/${id}/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTaskItem: (id, taskId) => request(`/tools/task-boards/${id}/tasks/${taskId}`, { method: 'DELETE' }),
  getTaskBoardLogs: (id, params = {}) => request(`/tools/task-boards/${id}/logs${buildQueryString(params)}`),
  getTaskBoardOpenToken: id => request(`/tools/task-boards/${id}/open-token`),
  rotateTaskBoardOpenToken: id => request(`/tools/task-boards/${id}/open-token`, { method: 'POST', body: '{}' }),
  revokeTaskBoardOpenToken: id => request(`/tools/task-boards/${id}/open-token`, { method: 'DELETE' }),
  getTaskBoardNotifyStatus: id => request(`/tools/task-boards/${id}/notify/status`),
  bindTaskBoardMiniNotify: (id, body = {}) => request(`/tools/task-boards/${id}/notify/mini-bind`, { method: 'POST', body: JSON.stringify(body || {}) }),
  ackTaskBoardMiniSubscribe: id => request(`/tools/task-boards/${id}/notify/mini-subscribe`, { method: 'POST', body: '{}' }),
  createTaskBoardOaBindQr: id => request(`/tools/task-boards/${id}/notify/oa-bind-qr`, { method: 'POST', body: '{}' }),
  getTaskBoardOaBindStatus: (id, ticketId) => request(`/tools/task-boards/${id}/notify/oa-bind-status${buildQueryString({ ticketId })}`),
  unbindTaskBoardOa: (id, targetId) => request(`/tools/task-boards/${id}/notify/oa-bind${buildQueryString(targetId ? { targetId } : {})}`, { method: 'DELETE' }),
  uploadTaskBoardImage: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const fingerprint = getDeviceFingerprint()
    fd.append('fingerprint', fingerprint)
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/tools/task-boards/upload-image`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      err.httpStatus = res.status
      throw err
    }
    return json
  },
  /** 邀请链接上传（无需网站登录，凭分享码） */
  viewUploadTaskBoardImage: async (code, file, password = '') => {
    const fd = new FormData()
    fd.append('file', file)
    if (password) fd.append('password', password)
    const fingerprint = getDeviceFingerprint()
    fd.append('fingerprint', fingerprint)
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(
      `${API_BASE}/tools/task-boards/view/${encodeURIComponent(code)}/upload-image`,
      { method: 'POST', headers, body: fd }
    )
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      err.httpStatus = res.status
      throw err
    }
    return json
  },
  getTaskBoardCard: code => request(`/tools/task-boards/view/${encodeURIComponent(code)}/card`),
  viewTaskBoard: (code, data = {}) => request(`/tools/task-boards/view/${encodeURIComponent(code)}`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  viewCreateTaskItem: (code, data) => request(`/tools/task-boards/view/${encodeURIComponent(code)}/tasks`, {
    method: 'POST', body: JSON.stringify(data)
  }),
  viewUpdateTaskItem: (code, taskId, data) => request(`/tools/task-boards/view/${encodeURIComponent(code)}/tasks/${taskId}`, {
    method: 'PUT', body: JSON.stringify(data)
  }),
  adminGetTaskBoardSettings: () => request('/admin/task-board-settings'),
  adminUpdateTaskBoardSettings: settings => request('/admin/task-board-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetTaskBoards: (params = {}) => request(`/admin/task-boards${buildQueryString(params)}`),
  adminGetTaskBoard: id => request(`/admin/task-boards/${id}`),
  adminUpdateTaskBoard: (id, data) => request(`/admin/task-boards/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteTaskBoard: id => request(`/admin/task-boards/${id}`, { method: 'DELETE' }),

  // 人声分离
  getVocalSeparates: () => request('/tools/vocal-separates'),
  getVocalSeparateQuota: () => request('/tools/vocal-separates/quota'),
  getVocalSeparate: id => request(`/tools/vocal-separates/${id}`),
  createVocalSeparate: async (file, stemMode = '2stem') => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('stemMode', stemMode)
    const fingerprint = getDeviceFingerprint()
    fd.append('fingerprint', fingerprint)
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/tools/vocal-separates`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '提交失败')
      err.code = json.code
      throw err
    }
    return json
  },
  downloadVocalSeparateTrack: async (id, key) => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(
      `${API_BASE}/tools/vocal-separates/${id}/download?key=${encodeURIComponent(key)}`,
      { headers }
    )
    if (!res.ok) {
      let msg = '下载失败'
      try {
        const json = await res.json()
        msg = json.message || msg
      } catch (e) { /* binary */ }
      throw new Error(msg)
    }
    return res.blob()
  },
  cancelVocalSeparate: id => request(`/tools/vocal-separates/${id}/cancel`, {
    method: 'POST',
    body: '{}'
  }),
  deleteVocalSeparate: id => request(`/tools/vocal-separates/${id}`, { method: 'DELETE' }),
  adminGetVocalSeparateSettings: () => request('/admin/vocal-separate-settings'),
  adminUpdateVocalSeparateSettings: settings => request('/admin/vocal-separate-settings', {
    method: 'PUT',
    body: JSON.stringify(settings)
  }),
  adminGetVocalSeparates: (params = {}) => request(`/admin/vocal-separates${buildQueryString(params)}`),
  adminCancelVocalSeparate: id => request(`/admin/vocal-separates/${id}/cancel`, {
    method: 'POST',
    body: '{}'
  }),
  adminDeleteVocalSeparate: id => request(`/admin/vocal-separates/${id}`, { method: 'DELETE' }),
  adminGetVocalSeparateNodes: () => request('/admin/vocal-separate-nodes'),
  adminEnsureVocalClusterToken: () => request('/admin/vocal-separate-cluster-token', { method: 'POST', body: '{}' }),
  adminRotateVocalClusterToken: () => request('/admin/vocal-separate-cluster-token/rotate', { method: 'POST', body: '{}' }),
  adminSetVocalNodeEnabled: (nodeKey, enabled) => request(
    `/admin/vocal-separate-nodes/${encodeURIComponent(nodeKey)}/enabled`,
    { method: 'PUT', body: JSON.stringify({ enabled: !!enabled }) }
  ),
  adminDeleteVocalNode: nodeKey => request(
    `/admin/vocal-separate-nodes/${encodeURIComponent(nodeKey)}`,
    { method: 'DELETE' }
  ),

  // 网站监控
  adminSiteMonitorOverview: (params = {}) => request(`/admin/site-monitor/overview${buildQueryString(params)}`),
  adminSiteMonitorSpiders: (params = {}) => request(`/admin/site-monitor/spiders${buildQueryString(params)}`),
  adminSiteMonitorClients: (params = {}) => request(`/admin/site-monitor/clients${buildQueryString(params)}`),
  adminSiteMonitorUris: (params = {}) => request(`/admin/site-monitor/uris${buildQueryString(params)}`),
  adminSiteMonitorIps: (params = {}) => request(`/admin/site-monitor/ips${buildQueryString(params)}`),
  adminSiteMonitorRegions: (params = {}) => request(`/admin/site-monitor/regions${buildQueryString(params)}`),
  adminSiteMonitorAttacks: (params = {}) => request(`/admin/site-monitor/attacks${buildQueryString(params)}`),
  adminSiteMonitorLogs: (params = {}) => request(`/admin/site-monitor/logs${buildQueryString(params)}`),
  adminGetSiteMonitorSettings: () => request('/admin/site-monitor/settings'),
  adminUpdateSiteMonitorSettings: settings => request('/admin/site-monitor/settings', {
    method: 'PUT',
    body: JSON.stringify(settings)
  }),
  adminPurgeSiteMonitor: () => request('/admin/site-monitor/purge', { method: 'POST', body: '{}' }),

  // 活码
  getLiveQrs: () => request('/tools/live-qrs'),
  getLiveQrQuota: () => request('/tools/live-qrs/quota'),
  createLiveQr: data => request('/tools/live-qrs', { method: 'POST', body: JSON.stringify(data) }),
  getLiveQr: id => request(`/tools/live-qrs/${id}`),
  updateLiveQr: (id, data) => request(`/tools/live-qrs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLiveQr: id => request(`/tools/live-qrs/${id}`, { method: 'DELETE' }),
  toggleLiveQr: (id, enabled) => request(`/tools/live-qrs/${id}/toggle`, {
    method: 'POST',
    body: JSON.stringify({ enabled })
  }),
  liveQrQrcode: id => request(`/tools/live-qrs/${id}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  getLiveQrCard: code => request(`/tools/live-qrs/view/${encodeURIComponent(code)}/card`),
  viewLiveQr: (code, data = {}) => request(`/tools/live-qrs/view/${encodeURIComponent(code)}`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  uploadLiveQrImage: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const fingerprint = getDeviceFingerprint()
    fd.append('fingerprint', fingerprint)
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/tools/live-qrs/upload-image`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  uploadLiveQrDocument: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const fingerprint = getDeviceFingerprint()
    fd.append('fingerprint', fingerprint)
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/tools/live-qrs/upload-document`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '文档上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  uploadLiveQrHtml: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const fingerprint = getDeviceFingerprint()
    fd.append('fingerprint', fingerprint)
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/tools/live-qrs/upload-html`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  /** AI 生成活码 HTML（耗时较长） */
  generateLiveQrHtml: (data = {}) => request('/tools/live-qrs/generate-html', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  getLiveQrFormSubmissions: (id, params = {}) =>
    request(`/tools/live-qrs/${id}/form-submissions${buildQueryString(params)}`),
  deleteLiveQrFormSubmission: (id, submissionId) =>
    request(`/tools/live-qrs/${id}/form-submissions/${submissionId}`, { method: 'DELETE' }),
  clearLiveQrFormSubmissions: id =>
    request(`/tools/live-qrs/${id}/form-submissions`, { method: 'DELETE' }),
  exportLiveQrFormSubmissions: async (id, params = {}) => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)
    const qs = buildQueryString({
      format: params.format || 'csv',
      mode: params.mode || 'split'
    })
    const res = await fetch(`${API_BASE}/tools/live-qrs/${id}/form-submissions/export${qs}`, { headers })
    if (!res.ok) {
      let msg = '导出失败'
      try {
        const j = await res.json()
        if (j && j.message) msg = j.message
      } catch (e) { /* ignore */ }
      throw new Error(msg)
    }
    const blob = await res.blob()
    const disp = res.headers.get('Content-Disposition') || ''
    const m = disp.match(/filename="?([^"]+)"?/i)
    const ext = (params.format === 'xlsx' || params.format === 'excel') ? 'xlsx' : 'csv'
    return { blob, filename: (m && m[1]) || `live-qr-form-${id}.${ext}` }
  },
  adminGetLiveQrSettings: () => request('/admin/live-qr-settings'),
  adminUpdateLiveQrSettings: settings => request('/admin/live-qr-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetLiveQrs: (params = {}) => request(`/admin/live-qrs${buildQueryString(params)}`),
  adminGetLiveQr: id => request(`/admin/live-qrs/${id}`),
  adminAuditLiveQr: (id, data) => request(`/admin/live-qrs/${id}/audit`, { method: 'PUT', body: JSON.stringify(data) }),
  adminUpdateLiveQr: (id, data) => request(`/admin/live-qrs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteLiveQr: id => request(`/admin/live-qrs/${id}`, { method: 'DELETE' }),

  // 收据
  getReceipts: (params = {}) => request(`/tools/receipts${buildQueryString(params)}`),
  getReceiptQuota: () => request('/tools/receipts/quota'),
  createReceipt: data => request('/tools/receipts', { method: 'POST', body: JSON.stringify(data) }),
  getReceipt: id => request(`/tools/receipts/${id}`),
  updateReceipt: (id, data) => request(`/tools/receipts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteReceipt: id => request(`/tools/receipts/${id}`, { method: 'DELETE' }),
  toggleReceipt: (id, enabled) => request(`/tools/receipts/${id}/toggle`, {
    method: 'POST',
    body: JSON.stringify({ enabled })
  }),
  getReceiptViews: (id, params = {}) => request(`/tools/receipts/${id}/views${buildQueryString(params)}`),
  receiptQrcode: id => request(`/tools/receipts/${id}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  getReceiptCard: code => request(`/tools/receipts/view/${encodeURIComponent(code)}/card`),
  receiptViewBoot: code => request(`/tools/receipts/view/${encodeURIComponent(code)}/boot`, {
    method: 'POST',
    body: JSON.stringify({})
  }),
  viewReceipt: async (code, data = {}) => {
    const payload = { ...data }
    const source = String(payload.source || 'h5').toLowerCase()
    if (source !== 'mini') {
      const bootRes = await request(`/tools/receipts/view/${encodeURIComponent(code)}/boot`, {
        method: 'POST',
        body: JSON.stringify({})
      })
      const boot = bootRes.data || bootRes
      const fingerprint = getDeviceFingerprint()
      const ticket = boot.ticket || ''
      const salt = boot.salt || ''
      let proof = ''
      const raw = `${ticket}|${fingerprint}|${salt}`
      if (typeof crypto !== 'undefined' && crypto.subtle) {
        const proofBuf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw))
        proof = Array.from(new Uint8Array(proofBuf))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')
      } else {
        throw new Error('当前浏览器不支持安全校验')
      }
      payload.ticket = ticket
      payload.proof = proof
      payload.guard = '1'
      payload.fingerprint = fingerprint
      if (!payload.pageUrl && typeof window !== 'undefined') {
        payload.pageUrl = window.location.href
      }
    }
    return request(`/tools/receipts/view/${encodeURIComponent(code)}`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: source === 'mini' ? undefined : { 'X-Receipt-Guard': '1' }
    })
  },
  verifyReceipt: (data = {}) => {
    const receiptNo = (data.receiptNo || data.no || data.code || data.verifyCode || '').toString().trim()
    if (receiptNo && !data.forcePost) {
      return request(`/tools/receipts/verify/${encodeURIComponent(receiptNo)}`)
    }
    return request('/tools/receipts/verify', {
      method: 'POST',
      body: JSON.stringify({
        receiptNo: data.receiptNo || data.no || '',
        verifyCode: data.verifyCode || data.code || ''
      })
    })
  },
  receiptViewEvent: (code, data = {}) => request(`/tools/receipts/view/${encodeURIComponent(code)}/event`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  uploadReceiptSeal: async file => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)

    // 先算哈希，已上传过则直接复用，免重复传文件
    try {
      const hash = await sha256File(file)
      if (hash) {
        const hit = await request('/tools/receipts/seal-by-hash', {
          method: 'POST',
          body: JSON.stringify({ hash })
        })
        if (hit && hit.code === 0 && hit.data) {
          return hit
        }
      }
    } catch (e) {
      // 未命中或哈希失败时走正常上传
    }

    const fd = new FormData()
    fd.append('file', file)
    fd.append('fingerprint', fingerprint)
    const res = await fetch(`${API_BASE}/tools/receipts/upload-seal`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  adminGetReceiptSettings: () => request('/admin/receipt-settings'),
  adminUpdateReceiptSettings: settings => request('/admin/receipt-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetReceipts: (params = {}) => request(`/admin/receipts${buildQueryString(params)}`),
  adminGetReceipt: id => request(`/admin/receipts/${id}`),
  adminUpdateReceipt: (id, data) => request(`/admin/receipts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteReceipt: id => request(`/admin/receipts/${id}`, { method: 'DELETE' }),

  // 报价单
  getQuotes: (params = {}) => request(`/tools/quotes${buildQueryString(params)}`),
  getQuoteQuota: () => request('/tools/quotes/quota'),
  createQuote: data => request('/tools/quotes', { method: 'POST', body: JSON.stringify(data) }),
  getQuote: id => request(`/tools/quotes/${id}`),
  updateQuote: (id, data) => request(`/tools/quotes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteQuote: id => request(`/tools/quotes/${id}`, { method: 'DELETE' }),
  toggleQuote: (id, enabled) => request(`/tools/quotes/${id}/toggle`, {
    method: 'POST',
    body: JSON.stringify({ enabled })
  }),
  quoteQrcode: id => request(`/tools/quotes/${id}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  getQuoteCard: code => request(`/tools/quotes/view/${encodeURIComponent(code)}/card`),
  viewQuote: (code, data = {}) => {
    const payload = { ...data }
    const keys = Object.keys(payload).filter(k => payload[k] !== undefined && payload[k] !== '')
    if (keys.length === 0) {
      return request(`/tools/quotes/view/${encodeURIComponent(code)}`)
    }
    if (keys.length === 1 && keys[0] === 'source') {
      return request(`/tools/quotes/view/${encodeURIComponent(code)}${buildQueryString(payload)}`)
    }
    return request(`/tools/quotes/view/${encodeURIComponent(code)}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  },
  quoteViewEvent: (code, data = {}) => request(`/tools/quotes/view/${encodeURIComponent(code)}/event`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  verifyQuote: (data = {}) => {
    const quoteNo = (data.quoteNo || data.no || data.code || '').toString().trim()
    if (quoteNo && !data.forcePost) {
      return request(`/tools/quotes/verify/${encodeURIComponent(quoteNo)}`)
    }
    return request('/tools/quotes/verify', {
      method: 'POST',
      body: JSON.stringify({
        quoteNo: data.quoteNo || data.no || '',
        code: data.code || ''
      })
    })
  },
  uploadQuoteSeal: async file => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)

    try {
      const hash = await sha256File(file)
      if (hash) {
        const hit = await request('/tools/quotes/seal-by-hash', {
          method: 'POST',
          body: JSON.stringify({ hash })
        })
        if (hit && hit.code === 0 && hit.data) {
          return hit
        }
      }
    } catch (e) {
      // 未命中或哈希失败时走正常上传
    }

    const fd = new FormData()
    fd.append('file', file)
    fd.append('fingerprint', fingerprint)
    const res = await fetch(`${API_BASE}/tools/quotes/upload-seal`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  adminGetQuoteSettings: () => request('/admin/quote-settings'),
  adminUpdateQuoteSettings: settings => request('/admin/quote-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetQuotes: (params = {}) => request(`/admin/quotes${buildQueryString(params)}`),
  adminGetQuote: id => request(`/admin/quotes/${id}`),
  adminUpdateQuote: (id, data) => request(`/admin/quotes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteQuote: id => request(`/admin/quotes/${id}`, { method: 'DELETE' }),

  // 电子合同
  getContracts: (params = {}) => request(`/tools/contracts${buildQueryString(params)}`),
  getContractQuota: () => request('/tools/contracts/quota'),
  createContract: data => request('/tools/contracts', { method: 'POST', body: JSON.stringify(data) }),
  getContract: id => request(`/tools/contracts/${id}`),
  updateContract: (id, data) => request(`/tools/contracts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContract: id => request(`/tools/contracts/${id}`, { method: 'DELETE' }),
  toggleContract: (id, enabled) => request(`/tools/contracts/${id}/toggle`, {
    method: 'POST',
    body: JSON.stringify({ enabled })
  }),
  contractQrcode: id => request(`/tools/contracts/${id}/qrcode`, { method: 'POST', body: JSON.stringify({}) }),
  getContractCard: code => request(`/tools/contracts/view/${encodeURIComponent(code)}/card`),
  viewContract: (code, data = {}) => {
    const payload = { ...data }
    const keys = Object.keys(payload).filter(k => payload[k] !== undefined && payload[k] !== '')
    if (keys.length === 0) {
      return request(`/tools/contracts/view/${encodeURIComponent(code)}`)
    }
    if (keys.length === 1 && keys[0] === 'source') {
      return request(`/tools/contracts/view/${encodeURIComponent(code)}${buildQueryString(payload)}`)
    }
    return request(`/tools/contracts/view/${encodeURIComponent(code)}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  },
  contractViewEvent: (code, data = {}) => request(`/tools/contracts/view/${encodeURIComponent(code)}/event`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  signContract: (code, data = {}) => request(`/tools/contracts/view/${encodeURIComponent(code)}/sign`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  uploadContractSeal: async file => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)

    try {
      const hash = await sha256File(file)
      if (hash) {
        const hit = await request('/tools/contracts/seal-by-hash', {
          method: 'POST',
          body: JSON.stringify({ hash })
        })
        if (hit && hit.code === 0 && hit.data) {
          return hit
        }
      }
    } catch (e) {
      // 未命中或哈希失败时走正常上传
    }

    const fd = new FormData()
    fd.append('file', file)
    fd.append('fingerprint', fingerprint)
    const res = await fetch(`${API_BASE}/tools/contracts/upload-seal`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  uploadContractSignature: async (fileOrDataUrl) => {
    const fingerprint = getDeviceFingerprint()
    const headers = { 'X-Device-Fingerprint': fingerprint }
    applyAuthHeaders(headers)

    if (typeof fileOrDataUrl === 'string') {
      return request('/tools/contracts/upload-signature', {
        method: 'POST',
        body: JSON.stringify({ dataUrl: fileOrDataUrl, fingerprint })
      })
    }

    const fd = new FormData()
    fd.append('file', fileOrDataUrl)
    fd.append('fingerprint', fingerprint)
    const res = await fetch(`${API_BASE}/tools/contracts/upload-signature`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  adminGetContractSettings: () => request('/admin/contract-settings'),
  adminUpdateContractSettings: settings => request('/admin/contract-settings', { method: 'PUT', body: JSON.stringify(settings) }),
  adminGetContracts: (params = {}) => request(`/admin/contracts${buildQueryString(params)}`),
  adminGetContract: id => request(`/admin/contracts/${id}`),
  adminUpdateContract: (id, data) => request(`/admin/contracts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteContract: id => request(`/admin/contracts/${id}`, { method: 'DELETE' }),

  getProductRecommends: (limit = 50) => request(`/product-recommends?limit=${limit}`),
  adminGetProductRecommends: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/product-recommends${qs ? `?${qs}` : ''}`)
  },
  adminCreateProductRecommend: data => request('/admin/product-recommends', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdateProductRecommend: (id, data) => request(`/admin/product-recommends/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteProductRecommend: id => request(`/admin/product-recommends/${id}`, { method: 'DELETE' }),
  adminUploadProductImage: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const headers = {}
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/admin/product-recommends/upload`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },

  // 评论
  getComments: articleId => request(`/articles/${articleId}/comments`),
  postComment: (articleId, data) => request(`/articles/${articleId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateComment: (id, data) => request(`/comments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteComment: id => request(`/comments/${id}`, { method: 'DELETE' }),
  uploadCommentImage: async file => {
    const fd = new FormData()
    fd.append('file', file)
    const headers = { 'X-Device-Fingerprint': getDeviceFingerprint() }
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/comments/upload`, { method: 'POST', headers, body: fd })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },

  // 浏览历史
  getHistory: (limit = 50) => request(`/history?limit=${limit}`),
  addHistory: article => request('/history', { method: 'POST', body: JSON.stringify(article) }),
  clearHistory: () => request('/history', { method: 'DELETE' }),

  // 后台管理
  adminDashboard: () => request('/admin/dashboard'),
  adminGetArticles: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/articles${qs ? `?${qs}` : ''}`)
  },
  adminGetArticle: id => request(`/admin/articles/${id}`),
  adminCreateArticle: data => request('/admin/articles', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdateArticle: (id, data) => request(`/admin/articles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteArticle: id => request(`/admin/articles/${id}`, { method: 'DELETE' }),
  adminGetUsers: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/users${qs ? `?${qs}` : ''}`)
  },
  adminGetUser: id => request(`/admin/users/${id}`),
  adminGetUserHistory: (id, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString()
    return request(`/admin/users/${id}/history${qs ? `?${qs}` : ''}`)
  },
  adminUpdateUser: (id, data) => request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminUpdateUserGrantedArticles: (id, articleIds) => request(`/admin/users/${id}/granted-articles`, {
    method: 'PUT',
    body: JSON.stringify({ articleIds })
  }),
  adminGrantUserArticle: (id, articleId) => request(`/admin/users/${id}/granted-articles`, {
    method: 'POST',
    body: JSON.stringify({ articleId })
  }),
  adminRevokeUserGrantedArticle: (id, articleId) => request(`/admin/users/${id}/granted-articles/${articleId}`, {
    method: 'DELETE'
  }),
  adminUpdateUserRole: (id, data) => request(`/admin/users/${id}/role`, { method: 'PUT', body: JSON.stringify(data) }),
  adminGetOrders: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/orders${qs ? `?${qs}` : ''}`)
  },
  adminGetMemberPlans: () => request('/admin/member-plans'),
  adminUpdateMemberPlans: plans => request('/admin/member-plans', { method: 'PUT', body: JSON.stringify({ plans }) }),
  adminGetMemberSettings: () => request('/admin/member-settings'),
  adminUpdateMemberSettings: settings => request('/admin/member-settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  adminGetPointsSettings: () => request('/admin/points-settings'),
  adminUpdatePointsSettings: settings => request('/admin/points-settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  adminGetCdkCodes: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/cdk-codes${qs ? `?${qs}` : ''}`)
  },
  adminCreateCdkCode: data => request('/admin/cdk-codes', { method: 'POST', body: JSON.stringify(data) }),
  adminGenerateCdkCodes: data => request('/admin/cdk-codes/generate', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdateCdkCode: (id, data) => request(`/admin/cdk-codes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteCdkCode: id => request(`/admin/cdk-codes/${id}`, { method: 'DELETE' }),
  adminGetSiteSettings: () => request('/admin/site-settings'),
  adminUpdateSiteSettings: settings => request('/admin/site-settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  adminGetStaticSettings: () => request('/admin/static-settings'),
  adminUpdateStaticSettings: settings => request('/admin/static-settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  adminGenerateStaticPages: () => request('/admin/static-settings/generate', { method: 'POST' }),

  // 评论管理
  adminGetComments: (params = {}) => request(`/admin/comments${buildQueryString(params)}`),
  adminMarkCommentsRead: ids => request('/admin/comments/mark-read', {
    method: 'POST',
    body: JSON.stringify({ ids })
  }),
  adminGetCommentSettings: () => request('/admin/comment-settings'),
  adminUpdateCommentSettings: settings => request('/admin/comment-settings', {
    method: 'PUT',
    body: JSON.stringify({ settings })
  }),
  adminUpdateComment: (id, data) => request(`/admin/comments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteComment: id => request(`/admin/comments/${id}`, { method: 'DELETE' }),

  // 友链管理
  adminGetFriendLinks: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/friend-links${qs ? `?${qs}` : ''}`)
  },
  adminGetFriendLink: id => request(`/admin/friend-links/${id}`),
  adminCreateFriendLink: data => request('/admin/friend-links', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdateFriendLink: (id, data) => request(`/admin/friend-links/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteFriendLink: id => request(`/admin/friend-links/${id}`, { method: 'DELETE' }),
  adminRegenerateFriendLinkCode: id => request(`/admin/friend-links/${id}/regenerate-code`, { method: 'POST' }),
  adminGetFriendLinkVisits: (id, params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return request(`/admin/friend-links/${id}/visits${qs ? `?${qs}` : ''}`)
  },
  adminDeleteFriendLinkVisit: visitId => request(`/admin/friend-links/visits/${visitId}`, { method: 'DELETE' }),
  adminClearFriendLinkVisits: id => request(`/admin/friend-links/${id}/visits`, { method: 'DELETE' }),
  adminUploadArticleImage: async (file, type = 'content') => {
    const fd = new FormData()
    fd.append('type', type)
    fd.append('file', file)
    const headers = {}
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/admin/articles/upload`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  },
  adminUploadSiteAsset: async (type, file) => {
    const fd = new FormData()
    fd.append('type', type)
    fd.append('file', file)
    const headers = {}
    applyAuthHeaders(headers)
    const res = await fetch(`${API_BASE}/admin/site-settings/upload`, {
      method: 'POST',
      headers,
      body: fd
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.code !== 0) {
      const err = new Error(json.message || '上传失败')
      err.code = json.code
      throw err
    }
    return json
  }
}

export default api
